/**
 * Painel de filtros horizontal para a página de Logs de Auditoria
 * Segue o padrão visual do sistema implantado nas listas de associados e empresas
 */


import { Search, Filter, Calendar, X, Download, RefreshCw } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';

interface LogsFilters {
  search: string;
  action: string;
  user: string;
  dateFrom: string;
  dateTo: string;
}

interface LogsFilterPanelProps {
  filters: LogsFilters;
  onFilterChange: (newFilters: Partial<LogsFilters>) => void;
  onClearFilters: () => void;
  uniqueActions: string[];
  uniqueUsers: string[];
  totalLogs: number;
  filteredCount: number;
  onRefresh: () => void;
  onExport: () => void;
}

export function LogsFilterPanel({
  filters,
  onFilterChange,
  onClearFilters,
  uniqueActions,
  uniqueUsers,
  totalLogs,
  filteredCount,
  onRefresh,
  onExport,
}: LogsFilterPanelProps) {
  const hasActiveFilters = 
    filters.search !== '' || 
    filters.action !== 'all' || 
    filters.user !== 'all' || 
    filters.dateFrom !== '' || 
    filters.dateTo !== '';

  return (
    <div className="space-y-4 mb-6">
      {/* Linha Superior: Ações Globais e Busca */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3 flex-1 min-w-[300px]">
          <div className="relative w-full max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar por ação, usuário ou detalhes..."
              value={filters.search}
              onChange={(e) => onFilterChange({ search: e.target.value })}
              className="pl-9 h-10"
            />
            {filters.search && (
              <button
                onClick={() => onFilterChange({ search: '' })}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          <div className="hidden md:flex items-center gap-2 text-sm text-muted-foreground ml-2">
            <span className="font-medium text-foreground">{filteredCount}</span>
            <span>de</span>
            <span>{totalLogs}</span>
            <span>logs</span>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={onRefresh} className="h-10">
            <RefreshCw className="h-4 w-4 mr-2" />
            Atualizar
          </Button>
          <Button variant="outline" size="sm" onClick={onExport} className="h-10">
            <Download className="h-4 w-4 mr-2" />
            Exportar
          </Button>
        </div>
      </div>

      {/* Linha Inferior: Filtros Específicos */}
      <div className="flex flex-wrap items-center gap-3 p-1">
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-medium">Filtrar por:</span>
        </div>

        {/* Action Select */}
        <div className="w-[200px]">
          <Select
            value={filters.action}
            onValueChange={(value) => onFilterChange({ action: value })}
          >
            <SelectTrigger className="h-9">
              <SelectValue placeholder="Todas as ações" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas as ações</SelectItem>
              {uniqueActions.map((action) => (
                <SelectItem key={action} value={action}>
                  {action.replace(/_/g, ' ').toUpperCase()}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* User Select */}
        <div className="w-[200px]">
          <Select
            value={filters.user}
            onValueChange={(value) => onFilterChange({ user: value })}
          >
            <SelectTrigger className="h-9">
              <SelectValue placeholder="Todos os usuários" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os usuários</SelectItem>
              {uniqueUsers.map((user) => (
                <SelectItem key={user} value={user}>
                  {user}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Date Filters */}
        <div className="flex items-center gap-2">
          <div className="relative">
            <Calendar className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              type="date"
              value={filters.dateFrom}
              onChange={(e) => onFilterChange({ dateFrom: e.target.value })}
              className="h-9 pl-8 w-[150px] text-xs"
            />
          </div>
          <span className="text-muted-foreground">até</span>
          <div className="relative">
            <Calendar className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              type="date"
              value={filters.dateTo}
              onChange={(e) => onFilterChange({ dateTo: e.target.value })}
              className="h-9 pl-8 w-[150px] text-xs"
            />
          </div>
        </div>

        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onClearFilters}
            className="h-9 text-xs text-muted-foreground hover:text-destructive"
          >
            Limpar Filtros
          </Button>
        )}
      </div>

      {/* Badges de Filtros Ativos */}
      {hasActiveFilters && (
        <div className="flex flex-wrap gap-2 pt-1 border-t border-border/50">
          {filters.action !== 'all' && (
            <Badge variant="secondary" className="gap-1 h-6">
              Ação: {filters.action.replace(/_/g, ' ').toUpperCase()}
              <X className="h-3 w-3 cursor-pointer" onClick={() => onFilterChange({ action: 'all' })} />
            </Badge>
          )}
          {filters.user !== 'all' && (
            <Badge variant="secondary" className="gap-1 h-6">
              Usuário: {filters.user}
              <X className="h-3 w-3 cursor-pointer" onClick={() => onFilterChange({ user: 'all' })} />
            </Badge>
          )}
          {(filters.dateFrom || filters.dateTo) && (
            <Badge variant="secondary" className="gap-1 h-6">
              Período: {filters.dateFrom || '...'} - {filters.dateTo || '...'}
              <X className="h-3 w-3 cursor-pointer" onClick={() => onFilterChange({ dateFrom: '', dateTo: '' })} />
            </Badge>
          )}
        </div>
      )}
    </div>
  );
}
